import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Typography from '@mui/material/Typography';
import subMinutes from 'date-fns/subMinutes';
import { type PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import { PaymentProviders } from '@getbento/payments/constants/providers';
import { usePaymentProvider } from '@getbento/payments/hooks/usePayments';
import { PaymentInformation } from 'payments/components/PaymentInformation';
import { useFeePolicy } from 'reservations/account/providers/ConfigProvider';
import { COMMON_DATETIME_FORMATS } from 'reservations/base/helpers/datetime';
import { getDateTime } from 'reservations/base/helpers/getDateTime';
import { getFeeText } from 'reservations/feePolicy/helpers';
import {
  DRAWER_STATES,
  useBookingForm
} from 'reservations/form/providers/BookingFormProvider';
import DeletePaymentMethodModal from 'reservations/payments/components/DeletePaymentMethodModal';
import { SavedPaymentMethodLabel } from 'reservations/payments/components/SavedPaymentMethodLabel';
import { useDinerPaymentMethods } from 'reservations/payments/hooks/useDinerPaymentMethods';

export const RequiredPayment = ({ children }: PropsWithChildren) => {
  const { control, setValue } = useFormContext();
  const [date, savedPaymentMethod] = useWatch({
    name: ['date', 'savedPaymentMethod']
  });
  const [paymentMethodToDelete, setPaymentMethodToDelete] = useState('');
  const { defaultPaymentMethodId, paymentMethods, deletePaymentMethod } =
    useDinerPaymentMethods();

  const handleSuccess = () => {
    if (paymentMethodToDelete) {
      deletePaymentMethod(paymentMethodToDelete);
      setPaymentMethodToDelete('');
    }
  };

  useEffect(() => {
    setValue('savedPaymentMethod', defaultPaymentMethodId);
  }, [defaultPaymentMethodId]);

  const paymentProvider = usePaymentProvider();
  const isClover = paymentProvider === PaymentProviders.CLOVER;
  const { setDrawerState } = useBookingForm();

  //TODO: Add tests in DMD-4966
  //istanbul ignore next
  const handleReplaceCard = () => {
    if (isClover) {
      setDrawerState(DRAWER_STATES.REPLACE_CARD);
    }
  };

  //TODO: Move back down to line 102 and add tests in DMD-4966
  //istanbul ignore next
  const newCardLabel = isClover ? 'Replace your card' : 'Add new card';

  return (
    <RequiredPaymentLayout>
      {paymentMethods.length > 0 && (
        <Box mb="20px" width="100%">
          <Controller
            control={control}
            name="savedPaymentMethod"
            render={({ field: { onChange, value, ...rest } }) => (
              <FormControl sx={{ width: '100%' }}>
                <RadioGroup {...rest} onChange={onChange} value={value}>
                  {paymentMethods.map(
                    ({ isExpirationValid, id, ...paymentMethod }) => (
                      <FormControlLabel
                        sx={{
                          '.MuiFormControlLabel-label': {
                            width: '100%'
                          }
                        }}
                        key={id}
                        disabled={!isExpirationValid}
                        value={id}
                        control={<Radio />}
                        label={
                          <SavedPaymentMethodLabel
                            {...paymentMethod}
                            id={id}
                            isExpirationValid={isExpirationValid}
                            handleDelete={setPaymentMethodToDelete}
                            showRemoveButton={!isClover}
                          />
                        }
                      />
                    )
                  )}
                  <FormControlLabel
                    value=""
                    control={<Radio />}
                    onClick={handleReplaceCard}
                    label={newCardLabel}
                  />
                </RadioGroup>
              </FormControl>
            )}
          />
          <DeletePaymentMethodModal
            isOpen={!!paymentMethodToDelete}
            onClose={() => setPaymentMethodToDelete('')}
            paymentMethodId={paymentMethodToDelete}
            handleSuccess={handleSuccess}
          />
        </Box>
      )}
      {!savedPaymentMethod ? (
        <PaymentInformation bookingDate={date}>{children}</PaymentInformation>
      ) : (
        children
      )}
    </RequiredPaymentLayout>
  );
};

export const RequiredPaymentLayout = ({ children }: PropsWithChildren) => (
  <Box>
    <Box mb="10px">
      <RequiredPaymentHeader />
      <CancellationFeeNotice />
    </Box>
    {children}
  </Box>
);

const RequiredPaymentHeader = () => (
  <Typography
    sx={theme => ({
      fontWeight: 500,
      fontSize: '16px',
      color: theme.palette.soy.dark,
      mb: '5px'
    })}
  >
    Credit card required
  </Typography>
);

export const CancellationFeeNotice = () => {
  const feePolicy = useFeePolicy();
  const [date, availability] = useWatch({
    name: ['date', 'availability']
  });

  const dateTime = useMemo(
    () => getDateTime(date, availability),
    [date, availability]
  );

  // No availability is only for tests which error out when one is not selected
  // Diners would need to select a time before being able to see this component
  if (!feePolicy.shouldCollect || !availability) {
    return null;
  }

  const cancellationDeadline = subMinutes(
    dateTime,
    feePolicy.cancelDeadlineMinutes
  );

  const perUnit = getFeeText(feePolicy.calcType);

  return (
    <Typography
      sx={theme => ({ fontSize: '14px', color: theme.palette.soy.dark })}
    >
      No-shows or cancellations after{' '}
      {COMMON_DATETIME_FORMATS.dayDateAndTime(cancellationDeadline)} are subject
      to a fee of ${feePolicy.fee} {perUnit}.
    </Typography>
  );
};
